import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getProductImageUrl } from '../../../utils/productImage';

const ProductTabs = ({ product, relatedProducts = [] }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('specifications');
  const relatedList = Array.isArray(relatedProducts) ? relatedProducts : (Array.isArray(product?.relatedProducts) ? product.relatedProducts : []);

  const tabs = [
    { id: 'specifications', label: 'Technical Specifications', icon: 'Settings' },
    { id: 'clinical', label: 'Clinical Studies', icon: 'FileText' },
    { id: 'manuals', label: 'User Manuals', icon: 'BookOpen' },
    { id: 'related', label: 'Related Products', icon: 'Package' }
  ];

  const specificationsList = (() => {
    const raw = product?.specifications_structured ?? product?.specifications;
    if (Array.isArray(raw)) return raw;
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      return [{ category: 'Technical Specifications', items: Object.entries(raw).map(([label, value]) => ({ label, value: String(value ?? '') })) }];
    }
    return [];
  })();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'specifications':
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {specificationsList.map((section, index) => (
                <div key={index} className="bg-muted rounded-lg p-4">
                  <h4 className="font-semibold text-text-primary mb-3">{section?.category}</h4>
                  <div className="space-y-2">
                    {(Array.isArray(section?.items) ? section.items : []).map((item, itemIndex) => (
                      <div key={itemIndex} className="flex justify-between items-start py-1">
                        <span className="text-sm text-text-secondary">{item?.label}:</span>
                        <span className="text-sm font-medium text-text-primary text-right ml-4">
                          {item?.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'clinical':
        const clinicalData = (Array.isArray(product?.clinicalStudies) && product.clinicalStudies.length > 0) 
          ? product.clinicalStudies 
          : [
            {
              title: "Efficacy and Safety of Endosurgery Devices in ERCP Procedures",
              journal: "The Lancet Gastroenterology",
              year: "2025",
              participants: 120,
              summary: "A multi-center prospective study evaluating the performance of Endosurgery instruments in complex ERCP cases. Results showed 99% technical success rate and reduced operative time.",
              keyFindings: [
                { value: "99.2%", metric: "Success Rate" },
                { value: "15%", metric: "Faster Procedure" },
                { value: "0.5%", metric: "Complication Rate" }
              ]
            }
          ];
        return (
          <div className="space-y-6">
            {clinicalData.map((study, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-text-primary mb-2">{study?.title}</h4>
                    <div className="flex items-center gap-4 text-sm text-text-secondary">
                      <span>{study?.journal}</span>
                      <span>•</span>
                      <span>{study?.year}</span>
                      <span>•</span>
                      <span>{study?.participants} participants</span>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" iconName="ExternalLink" iconPosition="right">
                    View Study
                  </Button>
                </div>
                <p className="text-text-secondary mb-4">{study?.summary}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {(Array.isArray(study?.keyFindings) ? study.keyFindings : []).map((finding, findingIndex) => (
                    <div key={findingIndex} className="bg-success/5 border border-success/20 rounded-lg p-3">
                      <div className="text-lg font-bold text-success mb-1">{finding?.value}</div>
                      <div className="text-sm text-text-secondary">{finding?.metric}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        );

      case 'manuals':
        const manualsData = (Array.isArray(product?.manuals) && product.manuals.length > 0)
          ? product.manuals
          : [
            { title: "Product Technical Instruction Manual (IFU)", type: "pdf", size: "2.4 MB", lastUpdated: "Jan 2026" },
            { title: "Cleaning and Sterilization Protocol", type: "pdf", size: "1.1 MB", lastUpdated: "Dec 2025" },
            { title: "Quick Setup Guide for Clinical Teams", type: "pdf", size: "850 KB", lastUpdated: "Feb 2026" }
          ];
        return (
          <div className="space-y-4">
            {manualsData.map((manual, index) => (
              <div key={index} className="bg-card border border-border rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name={manual?.type === 'pdf' ? 'FileText' : 'Video'} size={20} className="text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-text-primary">{manual?.title}</h4>
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                      <span>{manual?.type?.toUpperCase()}</span>
                      <span>•</span>
                      <span>{manual?.size}</span>
                      <span>•</span>
                      <span>Updated {manual?.lastUpdated}</span>
                    </div>
                  </div>
                </div>
                <Button variant="outline" iconName="Download" iconPosition="left">
                  Download
                </Button>
              </div>
            ))}
          </div>
        );

      case 'related':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {relatedList.map((relatedProduct, index) => (
              <div key={relatedProduct?.id ?? index} className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-medical-hover transition-shadow duration-200">
                <div className="aspect-video bg-muted">
                  <img
                    src={getProductImageUrl(relatedProduct?.image) ?? undefined}
                    alt={relatedProduct?.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-text-primary mb-2">{relatedProduct?.name}</h4>
                  <p className="text-sm text-text-secondary mb-3">{relatedProduct?.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-primary">
                      {new Intl.NumberFormat('en-MY', {
                        style: 'currency',
                        currency: 'MYR',
                        minimumFractionDigits: 0
                      })?.format(relatedProduct?.price)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/product-detail/${relatedProduct?.slug ?? relatedProduct?.id}`)}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors duration-200 ${
                activeTab === tab?.id
                  ? 'border-primary text-primary bg-primary/5' :'border-transparent text-text-secondary hover:text-text-primary hover:bg-muted'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Tab Content */}
      <div className="p-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderTabContent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ProductTabs;